import RecoveryCodesList from '@/components/Mfa/RecoveryCodesList';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';

interface RecoveryCodesDialogProps {
  open: boolean;
  /** New codes for an account that already had MFA, not first enrollment. */
  regenerated?: boolean;
  codes: string[];
  email?: string | null;
  onClose: () => void;
}

/**
 * Shows new recovery codes once. The person confirms they saved them before
 * the dialog closes, because the codes cannot be shown again.
 */
export default function RecoveryCodesDialog({
  open,
  regenerated = false,
  codes,
  email,
  onClose,
}: RecoveryCodesDialogProps): JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [saved, setSaved] = useState(false);

  function handleDone() {
    setSaved(false);
    onClose();
  }

  return (
    <Dialog
      open={open}
      aria-labelledby="recovery-codes-dialog-title"
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle id="recovery-codes-dialog-title">
        {regenerated
          ? 'Save your new recovery codes'
          : 'Save your recovery codes'}
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Alert severity={regenerated ? 'info' : 'success'} variant="outlined">
            {regenerated
              ? 'Your old recovery codes no longer work.'
              : 'Two-step verification is on.'}
          </Alert>
        </Box>
        <DialogContentText>
          If you lose your phone or security key, each recovery code signs you
          in once. Save them somewhere safe, such as a password manager.{' '}
          <strong>You will not see these codes again.</strong>
        </DialogContentText>
        <Box mt={3}>
          <RecoveryCodesList codes={codes} email={email} />
        </Box>
        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={saved}
                onChange={(event) => setSaved(event.target.checked)}
              />
            }
            label="I saved my recovery codes"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          size="large"
          disabled={!saved}
          onClick={handleDone}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
